import { NextContentfulImage } from "@components/Image";
import Lightbox from "@components/Lightbox/Lightbox";
import VideoPlayer from "@components/VideoPlayer";
import { getVideoUrl } from "@components/VideoPlayer/utils";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { useDevice } from "@utilities/react/get-device/get-device";
import RenderComponents from "@utilities/renderComponents";
import clsx from "clsx";
import { useContext, useEffect, useRef, useState } from "react";
import { useInView } from "react-hook-inview";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import IconCross from "../../../public/image/svg/cross.svg";
import styles from "../C253_StoriesContainer.module.scss";
import { StoryContext } from "../C253_StoriesProvider";

interface StoriesTextAndImageRightData {
  isBlue: boolean;
  enablePopupText: boolean;
  showChevron: boolean;
  h3Text: string;
  quoteImage: ImageWrapperType;
  quoteImageFull: ImageWrapperType;
  readMoreContent: any;
  storyHeading: any;
  isVimeoCode: boolean;
  videoCode: string;
  displayVideo: boolean;
}

export interface StoriesTextAndImageRightProps {
  content: StoriesTextAndImageRightData;
  index: number;
}

export const StoriesTextAndImageRight = (
  props: StoriesTextAndImageRightProps
) => {
  const { content, index } = props;
  const [readMoreOpen, setReadMoreOpen] = useState(false);
  const {
    isBlue,
    enablePopupText,
    showChevron,
    h3Text,//This field is used to display h2Text now and the label has been changed accordingly in contentful
    quoteImage,
    quoteImageFull,
    isVimeoCode,
    videoCode,
    displayVideo,
    readMoreContent,
    storyHeading,
  } = content;

  const lineRef = useRef<HTMLDivElement>(null);
  const mobileLineRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const isFirst = index === 0;

  const [showLightbox, setShowLightbox] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [hasLineAnimated, setHasLineAnimated] = useState(false);

  const { updateNavigation, lineHeights } = useContext(StoryContext);

  const device = useDevice();
  const isMobile = device === "mobile";

  const displayImage = quoteImageFull || quoteImage;

  const [ref, isVisible] = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (isVisible) {
      if (!hasAnimated) {
        setHasAnimated(true);
      }

      updateNavigation(index, !isBlue);
    }
  }, [isVisible, hasAnimated]);

  useEffect(() => {
    const getLineHeight = (ref) => {
      const lineY = ref.current.offsetTop;
      const sectionHeight = sectionRef.current.offsetHeight;
      const indent = isMobile ? 25 : 70;
      return sectionHeight - lineY + indent;
    };

    if (lineHeights[index] && !hasLineAnimated) {
      lineRef.current.style.height = getLineHeight(lineRef) + "px";
      mobileLineRef.current.style.height = getLineHeight(mobileLineRef) + "px";
      setHasLineAnimated(true);
    }
  }, [lineHeights]);

  const closeLightbox = () => {
    setTimeout(() => {
      setShowLightbox(false);
    }, 300);
  };

  const videoUrl = getVideoUrl(isVimeoCode ? "vimeo" : "youtube", videoCode);

  useEffect(() => {
    if (isMobile) {
      if (readMoreOpen) {
        document.querySelector("html")?.classList.add("story-opened");
      } else {
        document.querySelector("html")?.classList.remove("story-opened");
      }
    }
  }, [readMoreOpen]);

  return (
    <section
      ref={(node) => {
        ref(node);
        sectionRef.current = node;
      }}
      className={clsx(
        styles["small-quote"],
        isBlue && styles.blue,
        isBlue && "blue",
        quoteImageFull && styles["img-flush"],
        readMoreOpen && styles.open,
        hasAnimated && styles.animate
      )}
    >
      <div className="wrapper">
        <div
          className={clsx(
            styles.content,
            !quoteImageFull && quoteImage && styles.slim
          )}
        >
         { h3Text && h3Text.trim() !== "" && <h2
            className={clsx(styles.title, styles.h3)}
            dangerouslySetInnerHTML={{ __html: h3Text }}
          ></h2>}
          {storyHeading && RenderComponents(storyHeading)}
          {isFirst && <div className={clsx(styles.initial, styles.line)}></div>}
          {enablePopupText && (
            <a
              className={styles["read-more"]}
              href="#readmore"
              // aria-haspopup="true"
              onClick={(e) => {
                e.preventDefault();
                setReadMoreOpen((previousState) => !previousState);
              }}
            >
              Read more
            </a>
          )}
          <div ref={lineRef} className={styles.line}></div>
        </div>

        {displayVideo && videoCode ? (
          <div className={styles.picture}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowLightbox(true);
              }}
              className="play-on-image"
            >
              <div className="play-icon">
                <img src="/assets2019/img/vidicon.png" alt="play icon" />
                <span className={styles["visually-hidden"]}>Play video</span>
              </div>
              {displayImage && (
                <NextContentfulImage
                  alt={displayImage.fields.altText}
                  src={displayImage.fields.url}
                  height={displayImage.fields.height}
                  width={displayImage.fields.width}
                />
              )}
            </a>
          </div>
        ) : (
          displayImage && (
            <div className={styles.picture}>
              <NextContentfulImage
                alt={displayImage.fields.altText}
                src={displayImage.fields.url}
                height={displayImage.fields.height}
                width={displayImage.fields.width}
              />
            </div>
          )
        )}

        {enablePopupText && (
          <div className={styles["read-more-offscreen"]}>
            <PerfectScrollbar className={styles["read-more-inner"]}>
              <>{RenderComponents(readMoreContent)}</>
            </PerfectScrollbar>
            <button
              className={styles.close}
              tabIndex={0}
              aria-label="close search"
              onClick={() => setReadMoreOpen(false)}
            >
              <IconCross />
            </button>
          </div>
        )}
        {showLightbox && (
          <Lightbox isBanner onCloseFunction={closeLightbox}>
            <VideoPlayer url={videoUrl} />
          </Lightbox>
        )}
      </div>
      <div ref={mobileLineRef} className={clsx(styles.line, styles.mobile)} />
      {showChevron && (
        <button
          className={styles["slide-down"]}
          onClick={(e) => updateNavigation(index + 1, !isBlue, true, e.target)}
          aria-label="Scroll down"
        >
          <span className="chevron" />
        </button>
      )}
    </section>
  );
};

export default StoriesTextAndImageRight;
